/* eslint-disable no-case-declarations */
import React, { createContext, useReducer, useContext, useEffect } from 'react';
import LocaleContext from 'locales';
import downloadManager from '../utils/managers/downloadManager';

// 초기 상태
const initialState = {
  items: [],
  status: {},
  messages: {},
  visible: false,
  collapsed: false,
};

// 액션 타입
const ActionTypes = {
  ADD_ITEMS: 'ADD_ITEMS',
  UPDATE_STATUS: 'UPDATE_STATUS',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  UPDATE_FILE_NAME: 'UPDATE_FILE_NAME',
  REMOVE_ITEMS: 'REMOVE_ITEMS',
  TOGGLE_VISIBILITY: 'TOGGLE_VISIBILITY',
  TOGGLE_COLLAPSED: 'TOGGLE_COLLAPSED',
  CLEAR_COMPLETED: 'CLEAR_COMPLETED',
};

// 리듀서
function downloadReducer(state, action) {
  switch (action.type) {
    case ActionTypes.ADD_ITEMS:
      return {
        ...state,
        items: [...state.items, ...action.payload],
        visible: true,
      };

    case ActionTypes.UPDATE_STATUS:
      return {
        ...state,
        status: {
          ...state.status,
          [action.payload.id]: action.payload.status,
        },
      };

    case ActionTypes.UPDATE_MESSAGE:
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.id]: action.payload.message,
        },
      };
    case ActionTypes.UPDATE_FILE_NAME:
      return {
        ...state,
        items: state.items.map((item) =>
          item.uid === action.payload.id ? { ...item, name: action.payload.fileName } : item,
        ),
      };
    case ActionTypes.REMOVE_ITEMS:
      return {
        ...state,
        items: state.items.filter((item) => !action.payload.includes(item.uid)),
      };
    case ActionTypes.TOGGLE_VISIBILITY:
      return {
        ...state,
        visible: action.payload !== undefined ? action.payload : !state.visible,
      };
    case ActionTypes.TOGGLE_COLLAPSED:
      return {
        ...state,
        collapsed: action.payload !== undefined ? action.payload : !state.collapsed,
      };
    case ActionTypes.CLEAR_COMPLETED:
      const completedIds = state.items
        .filter((item) => ['completed', 'error', 'cancelled'].includes(state.status[item.uid]))
        .map((item) => item.uid);

      const newStatus = { ...state.status };
      const newMessages = { ...state.messages };

      completedIds.forEach((id) => {
        delete newStatus[id];
        delete newMessages[id];
      });

      return {
        ...state,
        items: state.items.filter((item) => !completedIds.includes(item.uid)),
        status: newStatus,
        messages: newMessages,
        visible: state.items.length > completedIds.length, // 남은 항목이 없으면 패널 숨기기
      };
    default:
      return state;
  }
}

// Context 생성
const DownloadContext = createContext();

// Provider 컴포넌트
export function DownloadProvider({ children }) {
  const [state, dispatch] = useReducer(downloadReducer, initialState);
  const { locale } = useContext(LocaleContext);

  // 컴포넌트가 마운트될 때 downloadManager에 상태 업데이트 함수 등록
  useEffect(() => {
    const updater = {
      addItems: (items) => {
        dispatch({ type: ActionTypes.ADD_ITEMS, payload: items });
      },

      updateStatus: (id, status) => {
        dispatch({
          type: ActionTypes.UPDATE_STATUS,
          payload: { id, status },
        });
      },

      updateMessage: (id, message) => {
        dispatch({
          type: ActionTypes.UPDATE_MESSAGE,
          payload: { id, message },
        });
      },
      updateFileName: (id, fileName) => {
        dispatch({
          type: ActionTypes.UPDATE_FILE_NAME,
          payload: { id, fileName },
        });
      },
      removeItems: (ids) => {
        dispatch({ type: ActionTypes.REMOVE_ITEMS, payload: ids });
      },
      clearCompleted: () => {
        dispatch({ type: ActionTypes.CLEAR_COMPLETED });
      },
      toggleVisibility: (visible) => {
        dispatch({ type: ActionTypes.TOGGLE_VISIBILITY, payload: visible });
      },
      getItems: () => state.items,
      getStatus: (id) => state.status[id],
    };
    downloadManager.setStateUpdater(updater);

    // 다운로드 매니저에 locale 주입
    if (locale && locale.resource) {
      downloadManager.setLocaleResource(locale.resource);
    }

    return () => {
      downloadManager.setStateUpdater(null);
    };
  }, [locale]);

  // Context 값
  const contextValue = {
    ...state,
    toggleVisibility: (visible) => {
      dispatch({ type: ActionTypes.TOGGLE_VISIBILITY, payload: visible });
    },
    toggleCollapsed: (collapsed) => {
      dispatch({ type: ActionTypes.TOGGLE_COLLAPSED, payload: collapsed });
    },
    clearCompleted: () => {
      dispatch({ type: ActionTypes.CLEAR_COMPLETED });
    },
    downloadFiles: downloadManager.downloadFiles,
    cancelDownload: downloadManager.cancelDownload,
  };

  return <DownloadContext.Provider value={contextValue}>{children}</DownloadContext.Provider>;
}

// Context 사용을 위한 커스텀 훅
export function useDownloadContext() {
  const context = useContext(DownloadContext);
  if (context === undefined) {
    throw new Error('useDownloadContext must be used within a DownloadProvider');
  }
  return context;
}
